import React, { useEffect, useState } from 'react';
import { adminAPI } from './services/api';
import RichTextEditor from './RichTextEditor';

interface Zone {
  id?: number;
  row_number: number;
  zone_number: number;
  label: string;
  sunbed_count: number;
  weekday_price: number;
  weekend_price: number;
  weekday_extra_person_fee: number;
  weekend_extra_person_fee: number;
  min_persons: number;
  max_persons: number;
  active: boolean;
  sort_order?: number;
  _new?: boolean;
}

interface GeneralCfg {
  weekend_days: number[];
  min_persons_per_zone: number;
  max_persons_per_zone: number;
  prices_include_vat: boolean;
  require_adjacent_zones: boolean;
}

const WEEK_DAY_LABELS = ['Pzr', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];

interface Props {
  sunbedFeaturesHTML: string;
  setSunbedFeaturesHTML: (v: string) => void;
}

const SunbedZonesTab: React.FC<Props> = ({ sunbedFeaturesHTML, setSunbedFeaturesHTML }) => {
  const [zones, setZones] = useState<Zone[]>([]);
  const [general, setGeneral] = useState<GeneralCfg>({
    weekend_days: [0, 6],
    min_persons_per_zone: 2,
    max_persons_per_zone: 3,
    prices_include_vat: true,
    require_adjacent_zones: false
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sunbedWebEnabled, setSunbedWebEnabled] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [res, salesSettingsRes] = await Promise.all([
        adminAPI.get('/admin/sunbed/zones'),
        adminAPI.get('/admin/settings/sunbed_public').catch(() => null)
      ]);
      if (res.data.success) {
        setZones((res.data.data.zones || []).map((z: any) => ({
          ...z,
          weekday_price: Number(z.weekday_price),
          weekend_price: Number(z.weekend_price),
          weekday_extra_person_fee: Number(z.weekday_extra_person_fee),
          weekend_extra_person_fee: Number(z.weekend_extra_person_fee)
        })));
        if (res.data.data.general) {
          setGeneral({
            weekend_days: res.data.data.general.weekend_days || [0, 6],
            min_persons_per_zone: res.data.data.general.min_persons_per_zone || 2,
            max_persons_per_zone: res.data.data.general.max_persons_per_zone || 3,
            prices_include_vat: res.data.data.general.prices_include_vat !== false,
            require_adjacent_zones: res.data.data.general.require_adjacent_zones === true
          });
        }
        if (res.data.data.features_html != null) {
          setSunbedFeaturesHTML(res.data.data.features_html);
        }
      }
      const salesSettings = salesSettingsRes?.data?.data?.value;
      setSunbedWebEnabled(salesSettings?.enabled !== false);
    } catch (e: any) {
      alert('Bölgeler yüklenemedi: ' + (e.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateSunbedWebEnabled = async (enabled: boolean) => {
    setSunbedWebEnabled(enabled);
    try {
      await adminAPI.updateSettings('sunbed_public', { enabled }, 'Şezlong web satış durumu');
      localStorage.setItem('sunbedPublicSettings', JSON.stringify({ enabled, updated_at: Date.now() }));
      window.dispatchEvent(new CustomEvent('sunbed-public-settings-changed', { detail: { enabled } }));
    } catch (e: any) {
      setSunbedWebEnabled(!enabled);
      alert('Şezlong web satış ayarı kaydedilemedi: ' + (e.response?.data?.message || e.message));
    }
  };

  const updateZone = (idx: number, patch: Partial<Zone>) => {
    setZones(prev => prev.map((z, i) => i === idx ? { ...z, ...patch } : z));
  };

  // Bir sıradaki tüm bölgelere aynı fiyatı uygula
  const applyToRow = (rowNumber: number, field: keyof Zone, value: number) => {
    setZones(prev => prev.map(z => z.row_number === rowNumber ? { ...z, [field]: value } : z));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        zones: zones.map((z, i) => ({
          ...z,
          sort_order: z.sort_order ?? (z.row_number * 100 + z.zone_number),
          id: z._new ? undefined : z.id
        })),
        general,
        features_html: sunbedFeaturesHTML
      };
      await adminAPI.put('/admin/sunbed/zones', payload);
      alert('✅ Şezlong bölge ayarları kaydedildi.');
      await load();
    } catch (e: any) {
      alert('Kaydetme hatası: ' + (e.response?.data?.message || e.message));
    } finally {
      setSaving(false);
    }
  };

  // Sıra başına gruplama
  const rowNumbers = Array.from(new Set(zones.map(z => z.row_number))).sort((a, b) => a - b);

  if (loading) return <div className="tab-content"><h2>🏖️ Şezlong Yönetimi</h2><p>Yükleniyor...</p></div>;

  return (
    <div className="tab-content">
      <h2>🏖️ Şezlong Bölge Yönetimi</h2>

      <div className="pricing-section" style={{ marginBottom: '1.5rem' }}>
        <h3>Web Satış Durumu</h3>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={sunbedWebEnabled}
            onChange={(e) => updateSunbedWebEnabled(e.target.checked)}
          />
          Şezlong web satışı aktif
        </label>
        <small style={{ display: 'block', marginTop: '0.35rem', color: '#666' }}>
          Kapalıyken şezlong menüsü ve rezervasyon sayfası ziyaretçilere gösterilmez.
        </small>
      </div>

      {/* Genel ayarlar */}
      <div className="pricing-section" style={{ marginBottom: '1.5rem' }}>
        <h3>Genel Ayarlar</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'center' }}>
          <div>
            <label style={{ fontWeight: 600, marginRight: '0.5rem' }}>Hafta sonu günleri:</label>
            {WEEK_DAY_LABELS.map((lbl, idx) => (
              <label key={idx} style={{ marginRight: '0.5rem', fontSize: '0.9rem' }}>
                <input
                  type="checkbox"
                  checked={general.weekend_days.includes(idx)}
                  onChange={e => {
                    setGeneral(g => ({
                      ...g,
                      weekend_days: e.target.checked
                        ? [...g.weekend_days, idx].sort()
                        : g.weekend_days.filter(d => d !== idx)
                    }));
                  }}
                /> {lbl}
              </label>
            ))}
          </div>
          <div>
            <label style={{ fontWeight: 600, marginRight: '0.5rem' }}>Bölge başına min kişi:</label>
            <input
              type="number" min={1} max={10}
              value={general.min_persons_per_zone}
              onChange={e => setGeneral(g => ({ ...g, min_persons_per_zone: parseInt(e.target.value) || 2 }))}
              style={{ width: 60, padding: '0.3rem' }}
            />
          </div>
          <div>
            <label style={{ fontWeight: 600, marginRight: '0.5rem' }}>Bölge başına max kişi:</label>
            <input
              type="number" min={1} max={10}
              value={general.max_persons_per_zone}
              onChange={e => setGeneral(g => ({ ...g, max_persons_per_zone: parseInt(e.target.value) || 3 }))}
              style={{ width: 60, padding: '0.3rem' }}
            />
          </div>
          <div>
            <label>
              <input
                type="checkbox"
                checked={general.prices_include_vat}
                onChange={e => setGeneral(g => ({ ...g, prices_include_vat: e.target.checked }))}
              /> Fiyatlar KDV dahil
            </label>
          </div>
          <div>
            <label title="Aktif olursa, 4+ kişi için seçilen bölgelerin yan yana (ardışık zone_number) olması zorunlu olur.">
              <input
                type="checkbox"
                checked={general.require_adjacent_zones}
                onChange={e => setGeneral(g => ({ ...g, require_adjacent_zones: e.target.checked }))}
              /> Çoklu bölge yan yana (bitişik) olmak zorunda
            </label>
          </div>
        </div>
        <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem', marginBottom: 0 }}>
          Bölge başına min/max kişi: 2 şezlong + 1 ek kişi (toplam 3) varsayılan. "Ek Kişi Ücreti" 3. kişi için taban fiyata eklenir.
        </p>
      </div>

      {/* Sıralar */}
      {rowNumbers.map(rowNumber => {
        const rowZones = zones.map((z, idx) => ({ z, idx })).filter(({ z }) => z.row_number === rowNumber);
        return (
          <div key={rowNumber} className="pricing-section" style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>
                {rowNumber}. Sıra {rowNumber === 1 ? '(Denize En Yakın)' : ''}
                <span style={{ fontSize: '0.85rem', color: '#666', fontWeight: 400, marginLeft: '0.5rem' }}>
                  · {rowZones.length} bölge · {rowZones.reduce((s, { z }) => s + (z.sunbed_count || 0), 0)} şezlong
                </span>
              </h3>
            </div>

            {/* Sıraya hızlı toplu fiyat ayarı */}
            <div style={{ background: '#f0f7ff', padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', fontSize: '0.85rem' }}>
              <strong>Tümüne uygula:</strong>
              <label>Taban — Hafta içi: <input type="number" min={0} style={{ width: 90 }}
                defaultValue={rowZones[0]?.z.weekday_price || 0}
                onBlur={e => applyToRow(rowNumber, 'weekday_price', parseFloat(e.target.value) || 0)} /></label>
              <label>Taban — Hafta sonu: <input type="number" min={0} style={{ width: 90 }}
                defaultValue={rowZones[0]?.z.weekend_price || 0}
                onBlur={e => applyToRow(rowNumber, 'weekend_price', parseFloat(e.target.value) || 0)} /></label>
              <label>Ek Kişi — Hafta içi: <input type="number" min={0} style={{ width: 80 }}
                defaultValue={rowZones[0]?.z.weekday_extra_person_fee || 0}
                onBlur={e => applyToRow(rowNumber, 'weekday_extra_person_fee', parseFloat(e.target.value) || 0)} /></label>
              <label>Ek Kişi — Hafta sonu: <input type="number" min={0} style={{ width: 80 }}
                defaultValue={rowZones[0]?.z.weekend_extra_person_fee || 0}
                onBlur={e => applyToRow(rowNumber, 'weekend_extra_person_fee', parseFloat(e.target.value) || 0)} /></label>
            </div>

            {/* Bölge tablosu */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                <thead>
                  <tr style={{ background: '#eceff1' }}>
                    <th style={th}>#</th>
                    <th style={th}>Etiket</th>
                    <th style={th}>Şezlong</th>
                    <th style={th} title="Bölgenin hafta içi (Pzt-Cum) 2 kişilik taban fiyatı">Taban — Hafta içi (₺)</th>
                    <th style={th} title="Bölgenin hafta sonu 2 kişilik taban fiyatı">Taban — Hafta sonu (₺)</th>
                    <th style={th} title="3. kişi geldiğinde hafta içi taban fiyata eklenecek ücret">Ek Kişi — Hafta içi (₺)</th>
                    <th style={th} title="3. kişi geldiğinde hafta sonu taban fiyata eklenecek ücret">Ek Kişi — Hafta sonu (₺)</th>
                    <th style={th}>Min/Max</th>
                    <th style={th}>Aktif</th>
                  </tr>
                </thead>
                <tbody>
                  {rowZones.map(({ z, idx }) => (
                    <tr key={z.id ?? `new-${idx}`} style={{ background: z._new ? '#fff8e1' : 'transparent' }}>
                      <td style={td}>
                        <input type="number" min={1} value={z.zone_number}
                          onChange={e => updateZone(idx, { zone_number: parseInt(e.target.value) || 1 })}
                          style={inp(60)} />
                      </td>
                      <td style={td}>
                        <input type="text" value={z.label}
                          onChange={e => updateZone(idx, { label: e.target.value })}
                          style={inp(180)} />
                      </td>
                      <td style={td}>
                        <input type="number" min={1} value={z.sunbed_count}
                          onChange={e => updateZone(idx, { sunbed_count: parseInt(e.target.value) || 2 })}
                          style={inp(60)} />
                      </td>
                      <td style={td}>
                        <input type="number" min={0} value={z.weekday_price}
                          onChange={e => updateZone(idx, { weekday_price: parseFloat(e.target.value) || 0 })}
                          style={inp(90)} />
                      </td>
                      <td style={td}>
                        <input type="number" min={0} value={z.weekend_price}
                          onChange={e => updateZone(idx, { weekend_price: parseFloat(e.target.value) || 0 })}
                          style={inp(90)} />
                      </td>
                      <td style={td}>
                        <input type="number" min={0} value={z.weekday_extra_person_fee}
                          onChange={e => updateZone(idx, { weekday_extra_person_fee: parseFloat(e.target.value) || 0 })}
                          style={inp(80)} />
                      </td>
                      <td style={td}>
                        <input type="number" min={0} value={z.weekend_extra_person_fee}
                          onChange={e => updateZone(idx, { weekend_extra_person_fee: parseFloat(e.target.value) || 0 })}
                          style={inp(80)} />
                      </td>
                      <td style={td}>
                        <input type="number" min={1} value={z.min_persons}
                          onChange={e => updateZone(idx, { min_persons: parseInt(e.target.value) || 2 })}
                          style={inp(45)} />
                        {' / '}
                        <input type="number" min={1} value={z.max_persons}
                          onChange={e => updateZone(idx, { max_persons: parseInt(e.target.value) || 3 })}
                          style={inp(45)} />
                      </td>
                      <td style={td}>
                        <input type="checkbox" checked={z.active}
                          onChange={e => updateZone(idx, { active: e.target.checked })} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* Şezlong özellikleri */}
      <div className="menu-section" style={{ marginTop: 30 }}>
        <h3>Şezlong Özellikleri (Müşteri Sayfasında Gözükür)</h3>
        <RichTextEditor value={sunbedFeaturesHTML} onChange={setSunbedFeaturesHTML} />
      </div>

      <div className="save-section" style={{ marginTop: '1.5rem' }}>
        <button onClick={save} className="btn-save-large" disabled={saving}>
          {saving ? '💾 Kaydediliyor...' : '💾 Bölge Ayarlarını Kaydet'}
        </button>
      </div>
    </div>
  );
};

const th: React.CSSProperties = { padding: '0.5rem', borderBottom: '2px solid #b0bec5', textAlign: 'left', fontSize: '0.8rem' };
const td: React.CSSProperties = { padding: '0.35rem 0.5rem', borderBottom: '1px solid #eceff1' };
const inp = (w: number): React.CSSProperties => ({
  width: w, padding: '0.3rem 0.4rem', fontSize: '0.85rem',
  border: '1px solid #cfd8dc', borderRadius: 4
});

export default SunbedZonesTab;
